"use client";

/**
 * ActivitiesComponent
 * Displays activity signup cards with booking links and built-in signup system
 */

import { LandingPageComponent, ActivitiesConfig, ActivityItem } from "@/lib/database/landing-pages";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { useMemo, useState, useEffect, useCallback, memo } from "react";
import { useParams } from "next/navigation";
import { X, MapPin, Clock, Users, ChevronDown, Calendar, Edit3 } from "lucide-react";
import ActivitySignupModal from "./ActivitySignupModal";

const HtmlContent = memo(({ html, className }: { html: string; className?: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
));
HtmlContent.displayName = "HtmlContent";

interface ActivitiesComponentProps {
  component: LandingPageComponent;
  primaryColor?: string;
  landingPageId?: number;
  pageContext?: string; // "main" or subpage slug - filters which time slots to show
}

// Helper to check if a URL is a Calendly link
const isCalendlyLink = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "calendly.com" || parsed.hostname.endsWith(".calendly.com");
  } catch {
    return false;
  }
};

export default function ActivitiesComponent({
  component,
  primaryColor,
  landingPageId,
  pageContext = "main",
}: ActivitiesComponentProps) {
  // Hooks
  const params = useParams();
  const slug = params.slug as string | undefined;

  // Config
  const config = (component.config || {}) as ActivitiesConfig;

  // Helper to filter time slots by page context
  const filterTimeSlotsByPage = useCallback(
    (activity: ActivityItem): ActivityItem => {
      if (!activity.timeSlots) return activity;

      const filteredSlots = activity.timeSlots.filter((slot) => {
        // If showOnPages is not set or empty, show on all pages
        if (!slot.showOnPages || slot.showOnPages.length === 0) return true;
        // Otherwise, only show if current page is in the list
        return slot.showOnPages.includes(pageContext);
      });

      return { ...activity, timeSlots: filteredSlots };
    },
    [pageContext]
  );

  // Color palette
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  // State
  const [calendlyModalUrl, setCalendlyModalUrl] = useState<string | null>(null);
  const [calendlyModalTitle, setCalendlyModalTitle] = useState<string>("");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [signupModalActivity, setSignupModalActivity] = useState<ActivityItem | null>(null);
  const [signupCounts, setSignupCounts] = useState<Record<string, Record<string, number>>>({});
  const [waitlistCounts, setWaitlistCounts] = useState<Record<string, Record<string, number>>>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  // Constants
  const title = config.title || "";
  const subtitle = config.subtitle || "";
  const calendlyLink = config.calendlyLink || "";
  const labels = config.labels || [
    { name: "Team Activity", color: "#5261EB" },
    { name: "Benchling for Good", color: "#10B981" },
  ];
  const allActivities = config.activities || [];

  // Helper to get label color
  const getLabelColor = (labelName: string) => {
    return labels.find((l) => l.name === labelName)?.color || "#6B7280";
  };

  // Filter out activities that haven't reached their publish time yet
  // AND filter time slots by page context
  const activities = allActivities
    .filter((activity) => {
      if (!activity.publishAt) return true; // No publish time = immediately visible
      return new Date(activity.publishAt) <= new Date();
    })
    .map(filterTimeSlotsByPage)
    // Hide activities that have no time slots visible on this page (for signup-enabled activities)
    .filter((activity) => {
      // If it's not a signup activity, always show it
      if (!activity.signupEnabled) return true;
      // If it has time slots visible on this page, show it
      if (activity.timeSlots && activity.timeSlots.length > 0) return true;
      // Otherwise, hide it (all time slots are for other pages)
      return false;
    });

  // Check if any activities have signup enabled
  const hasSignupActivities = activities.some((a) => a.signupEnabled && a.timeSlots?.length);

  // Fetch signup counts and waitlist counts for capacity display
  const fetchSignupCounts = useCallback(async () => {
    if (!landingPageId || !hasSignupActivities) return;

    setIsLoadingCounts(true);
    try {
      // Fetch both signup counts and waitlist counts in parallel
      const [signupResponse, waitlistResponse] = await Promise.all([
        fetch(`/api/landing-pages/activity-signups?landing_page_id=${landingPageId}`),
        fetch(`/api/landing-pages/activity-waitlist?landing_page_id=${landingPageId}`),
      ]);

      if (signupResponse.ok) {
        const signupData = await signupResponse.json();
        setSignupCounts(signupData.counts || {});
      }

      if (waitlistResponse.ok) {
        const waitlistData = await waitlistResponse.json();
        setWaitlistCounts(waitlistData.counts || {});
      }
    } catch (error) {
      console.error("Error fetching counts:", error);
    } finally {
      setIsLoadingCounts(false);
    }
  }, [landingPageId, hasSignupActivities]);

  // Fetch counts on mount and when activities change
  useEffect(() => {
    fetchSignupCounts();
  }, [fetchSignupCounts]);

  // Close modal on escape key and prevent body scroll
  useEffect(() => {
    if (!calendlyModalUrl) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCalendlyModalUrl(null);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [calendlyModalUrl]);

  // Handlers
  const handleBooking = (bookingLink?: string, activityTitle?: string) => {
    if (bookingLink && bookingLink !== "#") {
      if (isCalendlyLink(bookingLink)) {
        setCalendlyModalUrl(bookingLink);
        setCalendlyModalTitle(activityTitle || "Book Activity");
      } else {
        window.open(bookingLink, "_blank");
      }
    }
  };

  const handleCloseModal = () => {
    setCalendlyModalUrl(null);
    setCalendlyModalTitle("");
  };

  const toggleExpanded = (activityId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  const handleOpenSignupModal = (activity: ActivityItem) => {
    setSignupModalActivity(activity);
  };

  const handleCloseSignupModal = () => {
    setSignupModalActivity(null);
  };

  const handleSignupComplete = () => {
    // Refresh counts after successful signup
    fetchSignupCounts();
  };

  // Helper to get total spots remaining for an activity
  const getTotalSpotsRemaining = (activity: ActivityItem) => {
    if (!activity.signupEnabled || !activity.timeSlots?.length) return null;

    const activityCounts = signupCounts[activity.id || ""] || {};
    let totalRemaining = 0;
    let totalCapacity = 0;

    for (const slot of activity.timeSlots) {
      const count = activityCounts[slot.id] || 0;
      totalRemaining += Math.max(0, slot.capacity - count);
      totalCapacity += slot.capacity;
    }

    return { remaining: totalRemaining, total: totalCapacity };
  };

  // Check if signups are closed for an activity
  const isSignupClosed = (activity: ActivityItem) => {
    if (!activity.signupEnabled) return true;
    if (activity.signupsClosed) return true;
    if (activity.signupClosesAt && new Date(activity.signupClosesAt) < new Date()) return true;
    if (config.signupClosesAt && new Date(config.signupClosesAt) < new Date()) return true;
    const spots = getTotalSpotsRemaining(activity);
    return spots !== null && spots.remaining === 0;
  };

  // Render
  if (activities.length === 0) {
    return null;
  }

  return (
    <section id="activities" className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header - only show if there's content */}
        {(title || subtitle || calendlyLink) && (
          <div className="text-center mb-12">
            {title && (
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: palette.primary }}>
                {title}
              </h2>
            )}
            {subtitle && <p className="text-xl text-gray-600 max-w-3xl mx-auto">{subtitle}</p>}
            {calendlyLink && (
              <button
                type="button"
                onClick={() => {
                  setCalendlyModalUrl(calendlyLink);
                  setCalendlyModalTitle("Book Activities");
                }}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold active:opacity-80 touch-manipulation"
                style={{
                  backgroundColor: palette.primary,
                  color: "white",
                }}
                aria-label="View all activities and book"
                tabIndex={0}
              >
                <Calendar className="w-5 h-5" />
                View All & Book
              </button>
            )}
          </div>
        )}

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {activities.map((activity, index) => {
            const cardId = activity.id || `activity-${index}`;
            const isExpanded = expandedCards.has(cardId);
            const hasExpandableContent = activity.description && activity.description.length > 100;

            return (
              <div
                key={cardId}
                className="rounded-xl border-2 border-gray-100 flex flex-col overflow-hidden bg-white shadow-sm"
              >
                {/* Activity Image */}
                {activity.image && (
                  <div className="relative h-48 bg-gray-100 shrink-0">
                    <img
                      src={activity.image}
                      alt={activity.title || "Activity"}
                      className="w-full h-full object-cover"
                    />
                    {/* Label Badge on Image */}
                    {activity.label && (
                      <div
                        className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold text-white shadow-sm"
                        style={{ backgroundColor: getLabelColor(activity.label) }}
                      >
                        {activity.label}
                      </div>
                    )}
                  </div>
                )}

                {/* Activity Content */}
                <div className="p-5 flex flex-col grow">
                  {/* Label Badge (if no image) */}
                  {activity.label && !activity.image && (
                    <div className="mb-2">
                      <span
                        className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                        style={{ backgroundColor: getLabelColor(activity.label) }}
                      >
                        {activity.label}
                      </span>
                    </div>
                  )}

                  {/* Header with title and expand button */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{activity.title || "Untitled Activity"}</h3>
                    {hasExpandableContent && (
                      <button
                        onClick={() => toggleExpanded(cardId)}
                        className="shrink-0 p-2 -m-1 rounded-full active:bg-gray-100 touch-manipulation"
                        style={{
                          color: palette.primary,
                        }}
                        aria-label={isExpanded ? "Collapse description" : "Expand description"}
                        aria-expanded={isExpanded}
                        tabIndex={0}
                      >
                        <ChevronDown
                          className="w-5 h-5"
                          style={{
                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      </button>
                    )}
                  </div>

                  {/* Activity Details - compact inline display */}
                  {(() => {
                    // Get page-specific overrides if available
                    const pageOverride = activity.pageOverrides?.[pageContext];
                    const displayTime = pageOverride?.time || activity.time;
                    const displayDuration = pageOverride?.duration || activity.duration;
                    const displayCapacity = pageOverride?.capacity || activity.capacity;

                    return activity.meetingLocation || displayTime || displayDuration || displayCapacity ? (
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-xs text-gray-600">
                        {activity.meetingLocation && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" style={{ color: palette.primary }} />
                            {activity.meetingLocation}
                          </span>
                        )}
                        {displayTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" style={{ color: palette.primary }} />
                            {displayTime}
                            {displayDuration && ` (${displayDuration})`}
                          </span>
                        )}
                        {!displayTime && displayDuration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" style={{ color: palette.primary }} />
                            {displayDuration}
                          </span>
                        )}
                        {displayCapacity && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" style={{ color: palette.primary }} />
                            {displayCapacity}
                          </span>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* Description - with smooth expand/collapse animation */}
                  <div className="grow">
                    {activity.description && (
                      <div
                        className={isExpanded ? "" : "overflow-hidden"}
                        style={{
                          maxHeight: isExpanded ? "none" : "4rem",
                          transition: "max-height 0.4s ease-in-out",
                        }}
                      >
                        <HtmlContent
                          html={activity.description}
                          className="text-gray-600 text-sm prose prose-sm max-w-none prose-ul:my-1 prose-li:my-0 prose-p:my-1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Sign Up / Book Now Button */}
                  <div className="mt-4 mt-auto pt-4">
                    {(() => {
                      // Built-in signup system
                      if (activity.signupEnabled && activity.timeSlots?.length) {
                        const spots = getTotalSpotsRemaining(activity);
                        const closed = isSignupClosed(activity);

                        // Check if signups are explicitly closed (manual toggle or time-based)
                        const explicitlyClosed =
                          activity.signupsClosed ||
                          (activity.signupClosesAt && new Date(activity.signupClosesAt) < new Date()) ||
                          (config.signupClosesAt && new Date(config.signupClosesAt) < new Date());

                        // If signups are explicitly closed, show disabled state (overrides waitlist)
                        if (explicitlyClosed) {
                          return (
                            <div
                              className="w-full py-2.5 px-5 rounded-lg font-semibold text-center text-sm"
                              style={{
                                backgroundColor: "#f3f4f6",
                                color: "#9ca3af",
                              }}
                            >
                              Signups Closed
                            </div>
                          );
                        }

                        // If fully booked, show Join Waitlist button
                        if (spots && spots.remaining === 0) {
                          return (
                            <button
                              className="w-full py-3.5 sm:py-2.5 px-5 rounded-lg font-semibold text-base sm:text-sm flex items-center justify-center gap-2 min-h-[48px] sm:min-h-0 active:opacity-80 border-2"
                              style={{
                                backgroundColor: "white",
                                color: palette.primary,
                                borderColor: palette.primary,
                                touchAction: "manipulation",
                                WebkitTapHighlightColor: "transparent",
                              }}
                              onClick={() => handleOpenSignupModal(activity)}
                              aria-label={`Join waitlist for ${activity.title}`}
                              tabIndex={0}
                            >
                              Join Waitlist
                            </button>
                          );
                        }

                        return (
                          <button
                            className="w-full py-3.5 sm:py-2.5 px-5 rounded-lg font-semibold text-base sm:text-sm flex items-center justify-center gap-2 min-h-[48px] sm:min-h-0 active:opacity-80"
                            style={{
                              backgroundColor: palette.primary,
                              color: "white",
                              touchAction: "manipulation",
                              WebkitTapHighlightColor: "transparent",
                            }}
                            onClick={() => handleOpenSignupModal(activity)}
                            aria-label={`Sign up for ${activity.title}`}
                            tabIndex={0}
                          >
                            Sign Up
                            {spots && (
                              <span className="text-sm sm:text-xs opacity-80">
                                ({spots.remaining} spot{spots.remaining !== 1 ? "s" : ""} left)
                              </span>
                            )}
                          </button>
                        );
                      }

                      // External booking link or coming soon
                      if (activity.bookingLink === "#" || !activity.bookingLink) {
                        return (
                          <div
                            className="w-full py-2.5 px-5 rounded-lg font-semibold text-center text-sm"
                            style={{
                              backgroundColor: "#f3f4f6",
                              color: "#9ca3af",
                            }}
                          >
                            Coming Soon
                          </div>
                        );
                      }

                      return (
                        <button
                          className="w-full py-3.5 sm:py-2.5 px-5 rounded-lg font-semibold text-base sm:text-sm min-h-[48px] sm:min-h-0 active:opacity-80"
                          style={{
                            backgroundColor: palette.primary,
                            color: "white",
                            touchAction: "manipulation",
                            WebkitTapHighlightColor: "transparent",
                          }}
                          onClick={() => handleBooking(activity.bookingLink, activity.title)}
                          aria-label={`Book ${activity.title}`}
                          tabIndex={0}
                        >
                          Book Now
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Manage Signups Link - shown below activity cards when signup activities exist */}
        {hasSignupActivities && slug && (
          <div className="text-center mt-8">
            <a
              href={`/${slug}/activity-signup/request-link`}
              className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
              style={{ color: palette.primary }}
            >
              <Edit3 className="w-4 h-4" />
              Already signed up? Manage your signups
            </a>
          </div>
        )}
      </div>

      {/* Signup Modal */}
      {signupModalActivity && landingPageId && (
        <ActivitySignupModal
          activity={signupModalActivity}
          landingPageId={landingPageId}
          primaryColor={palette.primary}
          signupCounts={signupCounts[signupModalActivity.id || ""] || {}}
          waitlistCounts={waitlistCounts[signupModalActivity.id || ""] || {}}
          collectDietaryRestrictions={config.collectDietaryRestrictions}
          onClose={handleCloseSignupModal}
          onSignupComplete={handleSignupComplete}
        />
      )}

      {/* Calendly Modal */}
      {calendlyModalUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop button for closing */}
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm touch-manipulation"
            onClick={handleCloseModal}
            aria-label="Close modal"
            tabIndex={-1}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">{calendlyModalTitle}</h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors touch-manipulation"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Calendly Embed */}
            <div className="h-[calc(90vh-73px)]">
              <iframe
                src={calendlyModalUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title={`Calendly - ${calendlyModalTitle}`}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
